import type { LeadsTeamRef, LeadsWindowKey } from './LeadsLeaderboardMetrics';

export enum LeadsSortBy {
    Activity = 'activity',
    Credits = 'credits',
    CreditsPurchased = 'creditsPurchased',
    Messages = 'messages',
    EditorTime = 'editorTime',
    DaysActive = 'daysActive',
    Projects = 'projects',
    Components = 'components',
}

export type LeadTeamPlan = 'free' | 'plus';

export const ACTIVITY_HEARTBEAT_INTERVAL_MINUTES = 1;

export type LeadsDateRangePreset = 'last-7-days' | 'last-2-weeks' | 'last-30-days' | 'last-90-days';

export const LEADS_WINDOW_KEY_BY_PRESET: Record<LeadsDateRangePreset, LeadsWindowKey> = {
    'last-7-days': 'week',
    'last-2-weeks': 'twoWeeks',
    'last-30-days': 'thirtyDays',
    'last-90-days': 'ninetyDays',
};

export const MAX_PROFILE_HTML_LENGTH = 5_000_000;

export enum LeadProjectStage {
    Scoping = 's1',
    Speccing = 's2',
    SimValidated = 's3',
    BomClosure = 's4',
    Integration = 's5',
    Commissioning = 's6',
    Operating = 's7',
}

export const LeadProjectStageOptions: { value: LeadProjectStage; label: string }[] = [
    { value: LeadProjectStage.Scoping, label: 'S1 Scoping' },
    { value: LeadProjectStage.Speccing, label: 'S2 Speccing' },
    { value: LeadProjectStage.SimValidated, label: 'S3 Sim-validated' },
    { value: LeadProjectStage.BomClosure, label: 'S4 BOM closure' },
    { value: LeadProjectStage.Integration, label: 'S5 Integration' },
    { value: LeadProjectStage.Commissioning, label: 'S6 Commissioning' },
    { value: LeadProjectStage.Operating, label: 'S7 Operating' },
];

export enum LeadPersona {
    DesignerExpert = 'designer-expert',
    DesignerSupport = 'designer-support',
    Paas = 'paas',
    OemPartner = 'oem-partner',
}

export const LeadPersonaOptions: { value: LeadPersona; label: string }[] = [
    { value: LeadPersona.DesignerExpert, label: 'Designer-Expert (Heimans)' },
    { value: LeadPersona.DesignerSupport, label: 'Designer-Support (DC3)' },
    { value: LeadPersona.Paas, label: 'PAAS (Pheonix)' },
    { value: LeadPersona.OemPartner, label: 'OEM-Partner (Winline)' },
];

export enum LeadProductFit {
    Dcide = 'dcide',
    ExpertSupport = 'expert-support',
    ExpertSupportWhiteLabel = 'expert-support-white-label',
    Paas = 'paas',
    Ppl = 'ppl',
    PplPartnerProgram = 'ppl-partner-program',
    ProductCatalog = 'product-catalog',
}

export const LeadProductFitOptions: { value: LeadProductFit; label: string }[] = [
    { value: LeadProductFit.Dcide, label: 'DCIDE' },
    { value: LeadProductFit.ExpertSupport, label: 'Expert Support' },
    { value: LeadProductFit.ExpertSupportWhiteLabel, label: 'Expert Support (white-label)' },
    { value: LeadProductFit.Paas, label: 'PAAS' },
    { value: LeadProductFit.Ppl, label: 'DCIDE Controller' },
    { value: LeadProductFit.PplPartnerProgram, label: 'DCIDE Controller Partner Program' },
    { value: LeadProductFit.ProductCatalog, label: 'Product Catalog' },
];

export type RecentActivityEntry = {
    name: string;
    path: string;
    timestamp: number;
};

export type LeadRow = {
    userId: string;
    userName: string | null;
    userEmail: string | null;
    userType: string | null;
    goofy: string | null;
    teamId: string | null;
    teamName: string | null;
    teamPlan: LeadTeamPlan | null;
    teamPremium: boolean;
    creditsPurchased: number | null;
    creditsSpent: number;
    messagesSent: number;
    activityScore: number;
    editorMinutes: number;
    daysActive: number;
    lastActiveDay: string | null;
    projectsCount: number | null;
    componentsCount: number | null;
    memberTeams: LeadsTeamRef[] | null;
    latestReplayId: string | null;
    watchedReplayId: string | null;
    imageId: string | null;
    vip: boolean;
    notes: string | null;
    hasProfile: boolean;
    persona: LeadPersona | null;
    productFit: LeadProductFit[];
    projectStage: LeadProjectStage | null;
    hubspotUrl: string | null;
    recentActivity: RecentActivityEntry[]; // up to 10, newest first, deduped per event/path/minute (may be empty)
};

export type LeadsLeaderboardResponse = {
    windows: Record<LeadsWindowKey, LeadRow[]>;
};
